/**
 * Rol açıklaması (F1 TI-046): Kullanıcılar ekranında "Roller ne görür?" kartı ve rol değişikliği onayındaki kapsam cümlesi.
 * Metin SUNUCUNUN uyguladığı matristen (`@castinflow/shared` `ROLE_CAPABILITIES` + `ROLE_SCOPE`) üretilir; elle yazılmış bir
 * rol tablosu yoktur — rol kuralı değişirse açıklama da değişir.
 */
import { ShieldCheck } from 'lucide-react';
import {
  ROLE_CAPABILITIES,
  ROLE_SCOPE,
  USER_ROLES,
  type CapabilityName,
  type RoleScope,
  type UserRole,
} from '@castinflow/shared';
import { Card, CardBody, CardHeader } from '@castinflow/ui';
import { ROLE_LABEL } from '../../i18n/tr';

export const roleGuideText = {
  title: 'Roller ne görür?',
  description:
    'Rol, kullanıcının hangi kayıtları göreceğini ve ne yapabileceğini belirler. Çalışma alanı sahibi her zaman tam yetkilidir.',
  /**
   * Görünürlük kuralı sunucunun liste yüklemiyle aynıdır (`apps/api/src/repos/list-query.ts`): sistem yöneticisi her kaydı;
   * diğer rollerde kişi kendi kayıtlarını + kapsamındaki paylaşımları görür. Paylaşım = kayıt paylaşımı ya da departmanın
   * "yeni kayıtların varsayılan paylaşımı".
   */
  allRecords: 'Tüm kayıtları görür',
  scope: {
    all: 'Kendi kayıtlarını ve kendisiyle, departmanıyla ya da tüm çalışma alanıyla paylaşılan kayıtları görür',
    department:
      'Kendi kayıtlarını ve departmanında (alt departmanlar dâhil) paylaşılan kayıtları görür',
    own: 'Yalnız kendi kayıtlarını ve kendisine doğrudan paylaşılan kayıtları görür',
  } satisfies Record<RoleScope, string>,
  sharedToo:
    'Paylaşım, kaydın paylaşım ayarı ya da departmanın "yeni kayıtların varsayılan paylaşımı" ile yapılır (Departmanlar › Paylaşım ve onay).',
  can: 'Yapabildikleri',
  abilities: {
    write: 'kayıt oluşturur ve düzenler',
    writeLite: 'kayıtları sınırlı alanlarda düzenler',
    delete: 'kayıt siler',
    export: 'dışa aktarır',
    import: 'içe aktarır',
    users: 'kullanıcıları yönetir',
    settings: 'çalışma alanı ayarlarını değiştirir',
  },
  readOnly: 'kayıtları yalnız görüntüler',
  scopeChange: (from: string, to: string) =>
    from === to
      ? `Görebildiği kayıtlar değişmez: ${to.toLowerCase()}.`
      : `Görebildiği kayıtlar değişir: ${from.toLowerCase()} → ${to.toLowerCase()}.`,
};
const T = roleGuideText;

const ABILITY_ORDER: ReadonlyArray<readonly [CapabilityName, keyof typeof T.abilities]> = [
  ['records.write', 'write'],
  ['records.write.lite', 'writeLite'],
  ['records.delete', 'delete'],
  ['records.export', 'export'],
  ['records.import', 'import'],
  ['users.manage', 'users'],
  ['settings.manage', 'settings'],
];

/** Rolün yapabildikleri (sunucu matrisinden; `records.write` varken sınırlı yazma ayrıca yazılmaz) */
export function roleAbilities(role: UserRole): string[] {
  const caps = new Set<CapabilityName>(ROLE_CAPABILITIES[role]);
  const out: string[] = [];
  for (const [cap, key] of ABILITY_ORDER) {
    if (!caps.has(cap)) continue;
    if (key === 'writeLite' && caps.has('records.write')) continue;
    out.push(T.abilities[key]);
  }
  return out.length === 0 ? [T.readOnly] : out;
}

export const roleScopeText = (role: UserRole): string =>
  role === 'system_admin' ? T.allRecords : T.scope[ROLE_SCOPE[role]];

/** Rol değişikliği onayının kapsam cümlesi */
export const roleScopeChange = (from: UserRole, to: UserRole): string =>
  T.scopeChange(roleScopeText(from), roleScopeText(to));

const capitalize = (s: string) => s.charAt(0).toLocaleUpperCase('tr-TR') + s.slice(1);

export function RoleGuideCard() {
  return (
    <Card as="section" aria-labelledby="role-guide-title" testId="role-guide">
      <CardHeader
        titleId="role-guide-title"
        title={T.title}
        icon={<ShieldCheck size={18} />}
        description={T.description}
      />
      <CardBody>
        <dl className="space-y-3 text-sm">
          {USER_ROLES.map((r) => (
            <div key={r} data-testid={`role-guide-${r}`}>
              <dt className="font-semibold text-fg">{ROLE_LABEL[r]}</dt>
              <dd className="text-fg-muted">
                {roleScopeText(r)}. {capitalize(roleAbilities(r).join(', '))}.
              </dd>
            </div>
          ))}
        </dl>
        <p className="mt-3 text-sm text-fg-subtle">{T.sharedToo}</p>
      </CardBody>
    </Card>
  );
}
